import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../components/SafeIcon';
import SectionHeading from '../components/SectionHeading';
import ServiceCard from '../components/ServiceCard';
import ProjectCard from '../components/ProjectCard';
import { caseStudies, industries, stack, team, values, projects, services } from '../data/siteData';

const content = {
  about: ['About ProfitYo Global', 'A better way to connect the numbers, the systems, and the people behind your growth.', 'We are a team of accountants, strategists, and technologists who believe business transformation starts with understanding the whole picture.'],
  services: ['Our services', 'Practical expertise. Intelligent solutions. One connected partner.', 'We combine financial precision with modern technology to help businesses operate with more clarity, speed, and confidence.'],
  portfolio: ['Our portfolio', 'Ideas are only valuable when they create measurable progress.', 'Explore a selection of systems and solutions designed to remove friction and give growing teams room to focus.'],
  industries: ['Industries we serve', 'Experience that understands the way your business works.', 'Every industry has its own pressures, opportunities, and rhythms. Our approach adapts to the context behind the numbers.'],
  'case-studies': ['Case studies', 'From operational friction to measurable momentum.', 'See how the right combination of insight, automation, and technology can change what a business is capable of.'],
  technology: ['Technology stack', 'The right tools, thoughtfully connected.', 'We work across the platforms your business already trusts, while building the custom layers that move you forward.'],
};

export default function InnerPage({ type }) {
  const [eyebrow, title, text] = content[type];

  return (
    <>
      <section className="page-hero">
        <div className="container page-hero-inner">
          <span className="eyebrow">{eyebrow}</span>
          <h1>{title}</h1>
          <p>{text}</p>
        </div>
      </section>
      <div className="container page-content">
        {type === 'about' && <AboutContent />}
        {type === 'services' && <ServicesContent />}
        {type === 'portfolio' && <PortfolioContent />}
        {type === 'industries' && <IndustryContent />}
        {type === 'case-studies' && <CaseContent />}
        {type === 'technology' && <TechnologyContent />}
      </div>
    </>
  );
}

function AboutContent() {
  return (
    <div className="about-layout">
      <div className="about-lead">
        <h2>Finance and technology should work better together.</h2>
        <p>ProfitYo Global was built for businesses that need more than a vendor. We bring the financial discipline, strategic thinking, and technical fluency needed to make progress stick.</p>
        <p>Our work is grounded in partnership. We listen closely, make the complicated understandable, and build solutions that are useful long after launch.</p>
      </div>
      <div className="values-card">
        <span className="eyebrow">Our foundation</span>
        <h3>Mission</h3>
        <p>To help businesses grow with greater clarity, smarter systems, and a partner they can trust.</p>
        <h3>Vision</h3>
        <p>A world where every ambitious business has access to world-class financial and technology expertise.</p>
      </div>
      <div className="values-list">
        {values.map((value) => (
          <div key={value.number}>
            <b>{value.number}</b>
            <h3>{value.title}</h3>
            <p>{value.text}</p>
          </div>
        ))}
      </div>
      <div className="team-section">
        <SectionHeading eyebrow="Our team" title="People who understand the whole picture." />
        <div className="team-grid">
          {team.map(([title, text]) => (
            <div className="team-card" key={title}>
              <div className="team-avatar"><SafeIcon icon={FiIcons.FiUser} /></div>
              <h3>{title}</h3>
              <p>{text}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function ServicesContent() {
  return (
    <>
      <SectionHeading eyebrow="Capabilities" title="Everything you need to build with confidence." />
      <div className="service-grid all-services">
        {services.map((service, index) => <ServiceCard key={service.title} service={service} index={index} />)}
      </div>
    </>
  );
}

function PortfolioContent() {
  return (
    <>
      <SectionHeading eyebrow="Selected projects" title="Technology with a purpose." />
      <div className="project-grid portfolio-grid">
        {projects.map((project, index) => <ProjectCard key={project.name} project={project} index={index} />)}
      </div>
    </>
  );
}

function IndustryContent() {
  return (
    <>
      <SectionHeading eyebrow="Our perspective" title="Context makes the difference." />
      <div className="industry-grid">
        {industries.map(([name, icon]) => (
          <div className="industry-card" key={name}>
            <SafeIcon name={icon.replace('Fi', '')} />
            <h3>{name}</h3>
            <p>Focused solutions for the unique demands of {name.toLowerCase()} teams.</p>
            <SafeIcon icon={FiIcons.FiArrowUpRight} />
          </div>
        ))}
      </div>
    </>
  );
}

function CaseContent() {
  return (
    <div className="case-study-list">
      {caseStudies.map((item, index) => (
        <article className="case-study" key={item.client}>
          <div className="case-number">0{index + 1}</div>
          <div>
            <span className="eyebrow">{item.category}</span>
            <h2>{item.client}</h2>
            <div className="before-after">
              <div><small>Before</small><p>{item.before}</p></div>
              <div><small>After</small><p>{item.after}</p></div>
            </div>
          </div>
          <div className="case-metric"><strong>{item.metric}</strong><span>{item.label}</span></div>
        </article>
      ))}
    </div>
  );
}

function TechnologyContent() {
  return (
    <>
      <SectionHeading eyebrow="Our toolkit" title="Fluent in the systems that run modern business." />
      <div className="stack-grid">
        {stack.map((item, index) => (
          <div key={item}><span>0{(index % 6) + 1}</span><strong>{item}</strong><SafeIcon icon={FiIcons.FiArrowUpRight} /></div>
        ))}
      </div>
      <div className="tech-note">
        <SafeIcon icon={FiIcons.FiLayers} />
        <p>Technology is only useful when it serves the way your team actually works. We select, connect, and extend tools with that principle in mind.</p>
      </div>
    </>
  );
}